import {
	CmVisitScheduleOption,
	TreatmentConfigOptions,
	TreatmentConfigTemplate,
} from "../../../domain/treatmentPlan.interface";
import { normaliseDays, ProposedOptions } from "./draftToOptions";

/**
 * The draft the agent accumulates, as described by its output contract.
 *
 * Deliberately loose: the draft is partial for most of the conversation, and
 * the agent is a language model — a field can be absent, or arrive as a string
 * where a number is wanted. Everything that turns it into a saveable template
 * is checked here rather than assumed.
 */
export interface AgentConfigDraft {
	name?: string;
	duration?: number | string;
	visitsPerPeriod?: number | string;
	weeklyVisitsPerPeriod?: number | string;
	periodLength?: number | string;
	minHoursBetweenVisits?: number | string;
	behavior?: string;
	rewardEngineId?: string;
	cmVisitScheduleOptionIds?: string | number;
	segment?: number | string;
	range?: string;
	segments?: unknown[];
	/** Option lists the assistant wants the plan to offer. See draftToOptions. */
	options?: ProposedOptions;
}

export interface DraftConversionResult {
	template: TreatmentConfigTemplate | null;
	/** Why the draft cannot be applied yet. Empty when `template` is set. */
	blockers: string[];
}

/**
 * Find the schedule the draft refers to.
 *
 * By number first — that is what the reference list gives the agent. But a
 * schedule the agent proposed in the same turn is named by its days, and the
 * number it will be assigned is not knowable until the merge has happened, so
 * days are accepted as well.
 */
const findSchedule = (
	options: CmVisitScheduleOption[],
	chosen: string | number | undefined,
): CmVisitScheduleOption | undefined => {
	if (chosen === undefined || chosen === null || chosen === "")
		return undefined;
	const wanted = String(chosen).trim();

	const byNumber = options.find(
		(option) => String(option.cmVisitScheduleOptions) === wanted,
	);
	if (byNumber) return byNumber;

	const wantedDays = normaliseDays(wanted);
	if (!wantedDays) return undefined;
	return options.find(
		(option) => normaliseDays(option.cmVisitScheduleOptionsDays) === wantedDays,
	);
};

/**
 * A model may answer "12" or 12 for the same question. Anything that is not a
 * finite number after coercion is treated as absent, never as 0 — 0 is a real
 * value for some of these fields and silently substituting it would produce a
 * template that saves and then misbehaves.
 */
const toNumber = (value: number | string | undefined): number | null => {
	if (value === undefined || value === null || value === "") return null;
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Everything the draft has captured so far, over an empty template.
 *
 * The counterpart to `draftToTemplate` for a config that is not finished: it
 * fills in what is known and leaves the rest at the blank template's defaults,
 * so the manual editor can be seeded mid-conversation and finished by hand. No
 * blockers, because nothing is being saved — an empty field is just an empty
 * field.
 */
export const draftToPartialTemplate = (
	draft: AgentConfigDraft | null,
	planOptions: TreatmentConfigOptions | undefined,
	blank: TreatmentConfigTemplate,
): TreatmentConfigTemplate => {
	if (!draft) return blank;

	const schedule = findSchedule(
		planOptions?.cmVisitScheduleOptions ?? [],
		draft.cmVisitScheduleOptionIds,
	);
	const duration = toNumber(draft.duration);
	const visitsPerPeriod = toNumber(draft.visitsPerPeriod);

	return {
		...blank,
		name: draft.name?.trim() || blank.name,
		segment: toNumber(draft.segment) ?? blank.segment,
		range: draft.range?.trim() || (duration ? `1-${duration}` : blank.range),
		duration: duration ?? blank.duration,
		periodLength: toNumber(draft.periodLength) ?? blank.periodLength,
		weeklyVisitsPerPeriod:
			toNumber(draft.weeklyVisitsPerPeriod) ??
			visitsPerPeriod ??
			blank.weeklyVisitsPerPeriod,
		visitsPerPeriod: visitsPerPeriod ?? blank.visitsPerPeriod,
		minHoursBetweenVisits:
			toNumber(draft.minHoursBetweenVisits) ?? blank.minHoursBetweenVisits,
		behavior: draft.behavior?.trim() || blank.behavior,
		rewardEngineId: draft.rewardEngineId?.trim() || blank.rewardEngineId,
		cmVisitScheduleOptionIds: schedule
			? String(schedule.cmVisitScheduleOptions)
			: blank.cmVisitScheduleOptionIds,
		cmVisitSchedule: schedule
			? {
					cmVisitScheduleOptions: schedule.cmVisitScheduleOptions,
					cmVisitScheduleOptionsDays: schedule.cmVisitScheduleOptionsDays,
					cmVisitScheduleOptionsDaysDisplay:
						schedule.cmVisitScheduleOptionsDaysDisplay,
					cutDays: schedule.cutDays ?? [],
				}
			: blank.cmVisitSchedule,
	};
};

/**
 * Turn an agent draft into a template the plan can actually save.
 *
 * Returns blockers rather than throwing, and rather than filling gaps with
 * defaults: a config that is silently completed is the failure mode this whole
 * feature exists to avoid. The caller shows the blockers so the clinician can
 * ask the agent for the missing piece.
 */
export const draftToTemplate = (
	draft: AgentConfigDraft | null,
	planOptions: TreatmentConfigOptions | undefined,
	{ isFirstTemplate }: { isFirstTemplate: boolean },
): DraftConversionResult => {
	const blockers: string[] = [];
	if (!draft) {
		return { template: null, blockers: ["The assistant has no draft yet."] };
	}

	const duration = toNumber(draft.duration);
	const visitsPerPeriod = toNumber(draft.visitsPerPeriod);
	const minHoursBetweenVisits = toNumber(draft.minHoursBetweenVisits);

	// Trimmed once, here, so the value that is CHECKED is the value that is
	// USED. Re-trimming at the call site below is how a field can pass
	// validation and still be written blank.
	const name = draft.name?.trim() ?? "";
	const behavior = draft.behavior?.trim() ?? "";
	const rewardEngineId = draft.rewardEngineId?.trim() ?? "";

	if (!name) blockers.push("The config has no name.");
	if (duration === null) blockers.push("No duration (in weeks) was captured.");
	if (visitsPerPeriod === null)
		blockers.push("No visits per period was captured.");
	if (minHoursBetweenVisits === null) {
		blockers.push("No minimum hours between visits was captured.");
	}
	// The three the backend refuses to save without.
	if (!behavior) blockers.push("No behaviour event was chosen.");
	if (!rewardEngineId) {
		blockers.push("No reward engine was chosen.");
	}

	const schedule = findSchedule(
		planOptions?.cmVisitScheduleOptions ?? [],
		draft.cmVisitScheduleOptionIds,
	);
	if (!schedule) {
		blockers.push(
			draft.cmVisitScheduleOptionIds
				? `Visit schedule "${draft.cmVisitScheduleOptionIds}" is not one of this plan's schedules.`
				: "No visit schedule was chosen.",
		);
	}

	if (blockers.length > 0) return { template: null, blockers };

	// Everything below was already checked, but the checks above ACCUMULATE into
	// `blockers` instead of returning, and TypeScript cannot narrow across that.
	// Repeating them in a form it can follow costs one unreachable branch and
	// removes eight non-null assertions plus three `as number` casts — each of
	// which was a claim a reader had to re-verify against the list above, and
	// none of which the compiler was checking. Unreachable in practice: reaching
	// here means `blockers` is empty, which means every one of these passed.
	if (
		!name ||
		duration === null ||
		visitsPerPeriod === null ||
		minHoursBetweenVisits === null ||
		!behavior ||
		!rewardEngineId ||
		!schedule
	) {
		return {
			template: null,
			blockers: ["The config is incomplete."],
		};
	}

	const template: TreatmentConfigTemplate = {
		name,
		// The first template on a plan is the one enrollment falls back to.
		default: isFirstTemplate,
		segment: toNumber(draft.segment) ?? 1,
		range: draft.range?.trim() || `1-${duration}`,
		duration,
		periodLength: toNumber(draft.periodLength) ?? 1,
		weeklyVisitsPerPeriod:
			toNumber(draft.weeklyVisitsPerPeriod) ?? visitsPerPeriod,
		rewardEngineId,
		cmVisitScheduleOptionIds: String(schedule.cmVisitScheduleOptions),
		visitsPerPeriod,
		minHoursBetweenVisits,
		// Copied whole, not referenced: the template carries its own schedule so
		// editing the plan's option list later cannot silently repoint a config.
		cmVisitSchedule: {
			cmVisitScheduleOptions: schedule.cmVisitScheduleOptions,
			cmVisitScheduleOptionsDays: schedule.cmVisitScheduleOptionsDays,
			cmVisitScheduleOptionsDaysDisplay:
				schedule.cmVisitScheduleOptionsDaysDisplay,
			cutDays: schedule.cutDays ?? [],
		},
		behavior,
		additionalTreatmentConfigs: [],
	};

	return { template, blockers: [] };
};
